import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Autocomplete, TextField, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { TSearchAddCategory } from '../../types/global/forms.types';
import { CastingDirectorCard } from './CastingDirectorCard';
import { ICastingDirector } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { getCastingDirectors } from '../../redux/audition/actions';
import { useSelector } from 'react-redux';
import { castingDirectorSelector } from './selector';
import { BRAND_LUXE_PRIMARY } from '../../pages/BrandDashboard/brandDashboardTokens';
import { normalizeCastingDirectorRecord } from '../../utils/castingDirectorNormalize';

type SearchSelectComponentProps = {
    onChange: (newValue: any) => void;
    onAddClick: () => void;
    placeholder?: string;
    sx?: object;
    /** Merged onto the bordered search row wrapper (e.g. brand theme). */
    innerWrapperSx?: object;
    /** Merged into the Autocomplete dropdown Paper (listbox container). */
    listPaperSx?: object;
    /** Per-option row colors when overriding the default theme dropdown. */
    optionRowSx?: {
        backgroundColor: string;
        hoverBackgroundColor: string;
        /** Keyboard-focused / highlight state (defaults to hover color). */
        focusedBackgroundColor?: string;
        borderBottomColor: string;
    };
    /** Hide the trailing dropdown arrow (brand create-company row opens via field focus). */
    hidePopupIndicator?: boolean;
    multiple?: boolean;
    renderer?: React.ReactElement;
    category?: TSearchAddCategory;
    initialValue?: any;
};

/**
 *
 * Casting Director here also means company
 * so use this component as a company
 */

const SearchSelectComponent: React.FC<SearchSelectComponentProps> = ({
    onChange,
    onAddClick,
    placeholder = 'Search or select...',
    sx = {},
    innerWrapperSx = {},
    listPaperSx = {},
    optionRowSx,
    hidePopupIndicator = false,
    category,
    initialValue = null,
}) => {
    const { themeStyle } = useThemeContext();
    const [selected, setSelected] = useState<ICastingDirector | null>(initialValue);
    const dispatch = useDispatch();
    let value: ICastingDirector[] = [];
    const castingDirectors = useSelector(castingDirectorSelector);

    if (category === 'castingDirector' || category === 'company') {
        value = castingDirectors;
    }

    const handleOnChange = (newValue: ICastingDirector | null) => {
        const next = newValue ? normalizeCastingDirectorRecord(newValue) : null;
        setSelected(next);
        onChange && onChange(next);
    };

    const handleCancelClick = () => {
        setSelected(null);
    };

    const getValue = useCallback(() => {
        switch (category) {
            case 'castingDirector':
            case 'company':
                dispatch(getCastingDirectors());
                break;
        }
    }, [category, dispatch]);

    const Render = ({ item, index }: { item: ICastingDirector; index: number }) => {
        switch (category) {
            case 'castingDirector':
            case 'company':
                return <CastingDirectorCard mode="search" castingDirector={item} key={index} />;
            default:
                return <div />;
        }
    };

    useEffect(() => {
        getValue();
    }, [getValue]);

    useEffect(() => {
        if (initialValue && (category === 'castingDirector' || category === 'company')) {
            setSelected(normalizeCastingDirectorRecord(initialValue));
        } else {
            setSelected(initialValue ?? null);
        }
    }, [initialValue, category]);

    /** When Redux list is refreshed (e.g. after logo upload on create), keep the chip avatar in sync. */
    useEffect(() => {
        if (category !== 'castingDirector' && category !== 'company') return;
        if (!castingDirectors.length) return;
        setSelected(prev => {
            if (!prev?._id) return prev;
            const fresh = castingDirectors.find((c: ICastingDirector) => String(c._id) === String(prev._id));
            if (!fresh) return prev;
            const n = normalizeCastingDirectorRecord(fresh);
            if (!n) return prev;
            if (n.profile_picture === prev.profile_picture) return prev;
            return n;
        });
    }, [castingDirectors, category]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: theme[themeStyle].selectableBorder,
                        borderRadius: 2,
                        overflow: 'hidden',
                        display: 'flex',
                        width: '100%',
                        height: '48px',
                        alignItems: 'center',
                        backgroundColor: theme[themeStyle].form_fields,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: theme[themeStyle].highlight,
                            boxShadow: `0 0 0 1px ${theme[themeStyle].highlight}20`,
                        },
                        '&:focus-within': {
                            borderColor: theme[themeStyle].highlight,
                            boxShadow: `0 0 0 2px ${theme[themeStyle].highlight}30`,
                        },
                        ...innerWrapperSx,
                    }}>
                    <Autocomplete
                        value={selected}
                        onChange={(_, newValue) => handleOnChange(newValue)}
                        isOptionEqualToValue={(option, val) => !!val && option._id === val._id}
                        options={value}
                        getOptionLabel={option => option.company_name}
                        filterOptions={(options, { inputValue }) =>
                            options.filter(option =>
                                option.company_name.toLowerCase().includes(inputValue.toLowerCase()),
                            )
                        }
                        renderInput={params => (
                            <TextField
                                {...params}
                                placeholder={placeholder}
                                variant="standard"
                                InputProps={{
                                    ...params.InputProps,
                                    disableUnderline: true,
                                    sx: {
                                        fontSize: '14px',
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].tag_white,
                                        '&::placeholder': {
                                            color: theme[themeStyle].placeholderGray,
                                            opacity: 1,
                                        },
                                    },
                                }}
                                sx={{
                                    px: 2,
                                    '& .MuiInputBase-root': {
                                        backgroundColor: 'transparent',
                                    },
                                }}
                            />
                        )}
                        renderOption={(props, option) => {
                            const optBg = optionRowSx?.backgroundColor ?? theme[themeStyle].dropdown_black;
                            const optHover = optionRowSx?.hoverBackgroundColor ?? theme[themeStyle].role_hover;
                            const optFocus = optionRowSx?.focusedBackgroundColor ?? optHover;
                            const optBorder = optionRowSx?.borderBottomColor ?? theme[themeStyle].form_divider;
                            return (
                                <Box
                                    component="li"
                                    {...props}
                                    sx={{
                                        backgroundColor: optBg,
                                        borderBottom: `1px solid ${optBorder}`,
                                        py: 1.5,
                                        px: 2,
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': { backgroundColor: `${optHover} !important` },
                                        '&.Mui-focused': { backgroundColor: `${optFocus} !important` },
                                    }}>
                                    <Render item={option} index={value.indexOf(option)} />
                                </Box>
                            );
                        }}
                        renderTags={() =>
                            selected && (
                                <CastingDirectorCard
                                    castingDirector={selected}
                                    mode="display"
                                    onSelectionClick={handleCancelClick}
                                />
                            )
                        }
                        PaperComponent={({ children, ...props }) => (
                            <Paper
                                {...props}
                                sx={{
                                    backgroundColor: theme[themeStyle].dropdown_black,
                                    border: `1px solid ${theme[themeStyle].form_divider}`,
                                    borderRadius: 2,
                                    boxShadow: `0 8px 32px ${theme[themeStyle].shadowColor}`,
                                    mt: 1,
                                    maxHeight: 300,
                                    overflow: 'auto',
                                    '& .MuiAutocomplete-listbox': {
                                        padding: 0,
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiAutocomplete-option': {
                                        padding: 0,
                                    },
                                    ...listPaperSx,
                                }}>
                                {children}
                            </Paper>
                        )}
                        sx={{
                            width: '100%',
                            ...(hidePopupIndicator
                                ? {
                                      '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                                  }
                                : {
                                      '& .MuiAutocomplete-popupIndicator': {
                                          color: theme[themeStyle].tag_white,
                                      },
                                  }),
                            '& .MuiAutocomplete-clearIndicator': {
                                color: theme[themeStyle].tag_white,
                            },
                        }}
                    />
                </Box>
                <IconButton
                    onClick={onAddClick}
                    sx={{
                        borderRadius: 2,
                        backgroundColor: BRAND_LUXE_PRIMARY,
                        color: theme[themeStyle].true_white,
                        mx: 1,
                        width: 48,
                        height: 48,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            backgroundColor: theme[themeStyle].true_highlight,
                            transform: 'scale(1.05)',
                            boxShadow: `0 4px 12px ${theme[themeStyle].highlight}40`,
                        },
                    }}>
                    <AddIcon sx={{ color: theme[themeStyle].black }} />
                </IconButton>
            </Box>
            <Box mt={1}>
                {selected && (
                    <CastingDirectorCard
                        castingDirector={selected}
                        mode="display"
                        onSelectionClick={() => handleOnChange(null)}
                    />
                )}
            </Box>
        </Box>
    );
};

export default SearchSelectComponent;
